<script setup lang="ts">
import { PageShellSidebar } from '@hprofits/ui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import BannerFormatModal from '@/modules/campaigns/components/BannerFormatModal.vue';
import { BannerFormatSelection } from '@/modules/campaigns/composables/actions/useCreativeTypeFlow';
import { useCampaignGroupContext } from '@/modules/campaigns/composables/context/campaignGroupContextProvider';
import type { DirtyItemType } from '@/modules/campaigns/composables/mutations';
import { useCreativeTypeItems } from '@/modules/campaigns/views/Summary/composables/useCreativeTypeItems';

import CampaignMenu from './CampaignMenu.vue';

type Props = Readonly<{
  isCompactLayout: boolean;
}>;

const props = defineProps<Props>();
const isMobileMenuOpen = defineModel<boolean>('isMobileMenuOpen', { required: true });

const route = useRoute();

const {
  params,
  adUnitTypesSource,
  state: { hasLoadingOrPending, isDirty },
  actions: { submit },
  creativeCreation: { isCreating, isBannerModalOpen, selectType, selectBannerFormat, cancelBannerModal },
} = useCampaignGroupContext();

const creativeTypeItems = useCreativeTypeItems({ adUnitTypes: adUnitTypesSource.state.data, selectType });

// TODO: Use branded type and discriminated union here
type ActiveEntity = { kind: DirtyItemType; id: number };

// Only the currently-open entity can be dirty — its form is the one mounted;
// leaving it is intercepted by the unsaved-changes guard.
const activeEntity = computed<ActiveEntity | null>(() => {
  if (route.params.adId != null) {
    return { kind: 'ad', id: Number(route.params.adId) };
  }
  if (route.params.creativeId != null) {
    return { kind: 'creative', id: Number(route.params.creativeId) };
  }
  if (route.params.targetId != null) {
    return { kind: 'target', id: Number(route.params.targetId) };
  }
  return null;
});

const isItemDirty = (kind: DirtyItemType, id: string | number): boolean =>
  isDirty.value && activeEntity.value?.kind === kind && activeEntity.value?.id === Number(id);

const isSummaryDirty = computed<boolean>(() => activeEntity.value === null && isDirty.value);

const handleBannerFormatSelect = (selection: BannerFormatSelection): void => {
  void selectBannerFormat(selection);
};
</script>

<template>
  <PageShellSidebar>
    <CampaignMenu
      v-model:open="isMobileMenuOpen"
      :campaign-group-id="params.campaignGroupId"
      :mobile="props.isCompactLayout"
      :is-dirty="isSummaryDirty"
      :campaign-page-is-saving="hasLoadingOrPending"
      :is-item-dirty="isItemDirty"
      :creative-type-items="creativeTypeItems"
      :is-creating-creative="isCreating"
      @save="submit()"
    />
  </PageShellSidebar>

  <!-- Banner Format Modal -->
  <BannerFormatModal
    v-model:open="isBannerModalOpen"
    :is-loading="isCreating"
    @select="handleBannerFormatSelect"
    @cancel="cancelBannerModal"
  />
</template>
